/**
 * Loyalty Lane API client.
 * Uses fetch on all platforms (LL API supports CORS with no restrictions).
 * Attaches ClientToken header on every request; adds ShopperToken when authenticated.
 * Auto-refreshes CST on 401 and retries the original request once.
 */

import { TokenStorage } from '@/Coupons/lib/token-storage';

export class SessionExpiredError extends Error {
  constructor() {
    super('Your session has expired. Please sign in again.');
    this.name = 'SessionExpiredError';
  }
}

interface LoyaltyLaneRequestOptions {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  params?: Record<string, string | number | boolean>;
  data?: unknown;
  headers?: Record<string, string>;
  /** Skip auto-attaching auth tokens (used internally for token fetch calls). */
  skipAuth?: boolean;
}

interface LoyaltyLaneResponse<T = unknown> {
  data: T;
  status: number;
}

// Convert params to strings
function stringifyParams(
  params?: Record<string, string | number | boolean>,
): Record<string, string> | undefined {
  if (!params) return undefined;
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      result[key] = String(value);
    }
  }
  return result;
}

export const loyaltyLaneClient = {
  async request<T = unknown>(
    options: LoyaltyLaneRequestOptions,
    _isRetry = false,
  ): Promise<LoyaltyLaneResponse<T>> {
    const baseUrl = import.meta.env.VITE_LOYALTY_LANE_API;
    if (!baseUrl) {
      throw new Error('Missing VITE_LOYALTY_LANE_API environment variable');
    }

    // Build headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...options.headers,
    };

    if (!options.skipAuth) {
      const cst = await TokenStorage.getClientToken();
      headers['ClientToken'] = cst;

      const sst = TokenStorage.getShopperToken();
      if (sst) {
        headers['ShopperToken'] = sst;
      }
    }

    // Build URL
    const cleanBase = baseUrl.replace(/\/$/, '');
    const cleanPath = options.url.startsWith('/') ? options.url : `/${options.url}`;
    const fullUrl = `${cleanBase}${cleanPath}`;

    const fetchOptions: RequestInit = {
      method: options.method || 'GET',
      headers,
    };

    if (options.data !== undefined) {
      fetchOptions.body = JSON.stringify(options.data);
    }

    let fetchUrl = fullUrl;
    if (options.params) {
      const searchParams = new URLSearchParams(stringifyParams(options.params));
      fetchUrl = `${fullUrl}?${searchParams.toString()}`;
    }

    const response = await fetch(fetchUrl, fetchOptions);

    if (response.status === 401 && !_isRetry && !options.skipAuth) {
      return this.handleUnauthorized<T>(options);
    }

    if (response.status === 401 && _isRetry) {
      const body = await response.text().catch(() => '');
      if (body.includes('ShopperToken')) {
        TokenStorage.clearShopperToken();
        throw new SessionExpiredError();
      }
      throw new Error(`Loyalty Lane API error ${response.status}: ${body}`);
    }

    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(`Loyalty Lane API error ${response.status}: ${body}`);
    }

    // Extend local SST expiry on successful authenticated requests
    if (!options.skipAuth && TokenStorage.getShopperToken()) {
      TokenStorage.touchShopperExpiry();
    }

    const data: T = await response.json();
    return { data, status: response.status };
  },

  /** Handle 401 by refreshing the CST and retrying once. */
  async handleUnauthorized<T>(
    originalOptions: LoyaltyLaneRequestOptions,
  ): Promise<LoyaltyLaneResponse<T>> {
    TokenStorage.clearClientToken();
    await TokenStorage.getClientToken();
    return this.request<T>(originalOptions, true);
  },

  async get<T = unknown>(
    url: string,
    config?: {
      params?: Record<string, string | number | boolean>;
      headers?: Record<string, string>;
      skipAuth?: boolean;
    },
  ): Promise<LoyaltyLaneResponse<T>> {
    return this.request<T>({ url, method: 'GET', ...config });
  },

  async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: { headers?: Record<string, string>; skipAuth?: boolean },
  ): Promise<LoyaltyLaneResponse<T>> {
    return this.request<T>({ url, method: 'POST', data, ...config });
  },

  async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: { headers?: Record<string, string>; skipAuth?: boolean },
  ): Promise<LoyaltyLaneResponse<T>> {
    return this.request<T>({ url, method: 'PUT', data, ...config });
  },

  async delete<T = unknown>(
    url: string,
    config?: {
      params?: Record<string, string | number | boolean>;
      headers?: Record<string, string>;
      skipAuth?: boolean;
    },
  ): Promise<LoyaltyLaneResponse<T>> {
    return this.request<T>({ url, method: 'DELETE', ...config });
  },
};
